import React, { useContext, useEffect, useState, useRef } from "react"
import i18n from "i18n"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Box,
  DialogActions,
  Link,
  IconButton,
  Backdrop,
  CircularProgress,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import MarginTab from "./margin/MarginTab"
import DiscountTab from "./margin/DiscountTab"
import FormulasTab from "./margin/FormulasTab"
import { useMarginsDiscounts } from "hooks/margin/useMarginsDiscounts"
import Budget2023Context from "context/budget2023/Budget2023Context"
import Button from "@mui/material/Button"
import BajasTab from "./margin/BajasTab"

const recalcModalState = {
  isOpen: false,
  projectId: null,
  setOpen: (isOpen, projectId) => {
    recalcModalState.isOpen = isOpen
    recalcModalState.projectId = projectId
    recalcModalState.listeners.forEach(listener => listener(isOpen, projectId))
  },
  listeners: [],
}

const useRecalcModalState = () => {
  const [isOpen, setIsOpen] = useState(recalcModalState.isOpen)
  const [projectId, setProjectId] = useState(recalcModalState.projectId)

  useEffect(() => {
    const handleChange = (open, projectId) => {
      setIsOpen(open)
      setProjectId(projectId)
    }

    recalcModalState.listeners.push(handleChange)

    return () => {
      recalcModalState.listeners = recalcModalState.listeners.filter(
        listener => listener !== handleChange,
      )
    }
  }, [])

  // Función para abrir el modal de recalculación
  const openRecalcModal = projectId => {
    recalcModalState.setOpen(true, projectId)
  }

  // Función para cerrar el modal de recalculación
  const closeRecalcModal = () => {
    recalcModalState.setOpen(false, null)
  }

  return { isOpen, projectId, openRecalcModal, closeRecalcModal }
}

const scrollContentStyle = { flex: 1, overflowY: "auto", paddingTop: 1 }
const stickyMenuStyle = {
  position: "sticky",
  top: 0,
  backgroundColor: "white",
  zIndex: 1,
}
const dialogContentStyle = {
  display: "flex",
  flexDirection: "column",
  height: "70vh",
}
const iconBtnStyle = { position: "absolute", right: 8, top: 8 }

const buildTabConfig = isBudgetClosed => [
  {
    label: i18n.t("marginsModal.marginTitle"),
    component: <MarginTab isBudgetClosed={isBudgetClosed} />,
  },
  {
    label: i18n.t("marginsModal.discountTitle"),
    component: <DiscountTab isBudgetClosed={isBudgetClosed} />,
  },
  {
    label: i18n.t("bajasTab.title"),
    component: <BajasTab isBudgetClosed={isBudgetClosed} />,
  },
  {
    label: i18n.t("marginsModal.formulaTitle"),
    component: <FormulasTab isBudgetClosed={isBudgetClosed} />,
  },
]

const MarginsModal = () => {
  const { state, setTab, saveConfig, marginModal, loadInitialData } =
    useMarginsDiscounts()
  const { isBudgetClosed } = useContext(Budget2023Context)
  const { tab } = state
  const { open, setMarginModal, projectId } = marginModal
  const tabConfig = buildTabConfig(isBudgetClosed)

  useEffect(() => {
    loadInitialData()
  }, [])

  async function saveMarginsConfig(projectId) {
    if (!projectId) return

    const response = await saveConfig(projectId)

    if (!response) return

    setMarginModal(false, null)
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={() => setMarginModal(false, null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ "data-testid": "margins-modal" }}
      >
        <DialogTitle>
          {i18n.t("marginsModal.title")}
          <IconButton
            aria-label="close"
            onClick={() => setMarginModal(false, null)}
            sx={iconBtnStyle}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        {state.loading && (
          <Backdrop
            open={true}
            sx={{ zIndex: theme => theme.zIndex.modal + 1 }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
        <DialogContent sx={dialogContentStyle}>
          <Box sx={stickyMenuStyle}>
            <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)}>
              {tabConfig.map((t, index) => (
                <Tab key={index} label={t.label} />
              ))}
            </Tabs>
          </Box>

          <Box sx={scrollContentStyle}>{tabConfig[tab].component}</Box>
        </DialogContent>
        <DialogActions>
          <Box textAlign="center" width="100%">
            <Button
              color="branded"
              variant="contained"
              size="large"
              onClick={() => saveMarginsConfig(projectId)}
              data-element="saveMarginsBtn"
              data-testid="margins-modal-save-button"
              disabled={isBudgetClosed}
              autoFocus
            >
              {i18n.t("buttons.save")}
            </Button>
            <Box mt={1}>
              <Link
                href="https://help.planhopper.com/es/articles/12794120-margenes-descuentos-bajas-y-formulas"
                target="_blank"
                rel="noopener noreferrer"
              >
                {i18n.t("marginsModal.link")}
              </Link>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default MarginsModal
export { useRecalcModalState }
